import { ReactNode, useEffect, useState, createContext, useContext } from 'react';
import ReactPixel from 'react-facebook-pixel';
import { useAppConfig } from '../hooks/useAppConfig';
import { centsToFloat } from '../functions/price_functions';

type PluginFacebook = {
    able: boolean;
    facebookPixelId: string;
}

type FacebookPixelContextData = {
    pluginFacebook: PluginFacebook;
    trackPageView: () => void;
    trackAddToCart: () => void;
    trackInitiateCheckout: () => void;
    trackPurchase: (value: number) => void;
}

type FacebookPixelContextProviderProps = {
    children: ReactNode;
};

export const FacebookPixelContext = createContext({} as FacebookPixelContextData);

export function FacebookPixelContextProvider({ children }: FacebookPixelContextProviderProps) {

    const useApp = useAppConfig();

    const [pluginFacebook, setPluginFacebook] = useState<PluginFacebook>({
        able: false,
        facebookPixelId: "",
    });

    async function getPluginFacebook() {
        try {
            const plugins = await useApp.getPluginconfig();
            const pluginFacebookConfig = plugins.find(
                (plugin) => [269, 268, 267].includes(plugin.plugintemplateId)
            );
            if (pluginFacebookConfig) {
                const content = pluginFacebookConfig.content;
                setPluginFacebook({
                    able: content.able.toString() === '1',
                    facebookPixelId: content.pixel || "",
                });
            }
        } catch (error) {
            // handle error if needed
        }
    }

    async function initFacebookPixel() {
        try {
            if (pluginFacebook.able && pluginFacebook.facebookPixelId) {
                ReactPixel.init(pluginFacebook.facebookPixelId);
                // Dispara PageView inicial automaticamente após init
                ReactPixel.track('PageView');
            }
        } catch (error) {
            console.log('Error tracking initFacebookPixel:', error);

        }
    }

    // Evento 1: Visualizações da tela inicial da loja
    function trackPageView() {
        try {
            if (pluginFacebook.able) {
                ReactPixel.track('PageView');
            }
        } catch (error) {
            console.log('Error tracking trackPageView:', error);
        }
    }

    // Evento 2: Add produto ao carrinho
    function trackAddToCart() {
        try {
            if (pluginFacebook.able) {
                ReactPixel.track('PageView');
                ReactPixel.track('AddToCart');
            }
        } catch (error) {
            console.log('Error tracking trackAddToCart:', error);
        }
    }

    // Evento 3: Início do checkout
    function trackInitiateCheckout() {
        try {
            if (pluginFacebook.able) {
                ReactPixel.track('PageView');
                ReactPixel.track('InitiateCheckout');
            }
        } catch (error) {
            console.log('Error tracking InitiateCheckout:', error);
        }
    }

    // Evento 4: Evento de compra
    function trackPurchase(value: number) {
        try {
            if (pluginFacebook.able) {
                ReactPixel.track('PageView');
                ReactPixel.track('Purchase', {
                    value: centsToFloat(value),
                    currency: 'BRL'
                });
            }
        } catch (error) {
            console.log('Error tracking trackPurchase:', error);

        }
    }

    useEffect(() => {
        getPluginFacebook();
    }, []);

    useEffect(() => {
        if (pluginFacebook.able && pluginFacebook.facebookPixelId) {
            initFacebookPixel();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pluginFacebook.able, pluginFacebook.facebookPixelId]);



    return (
        <FacebookPixelContext.Provider value={{
            pluginFacebook,
            trackPageView,
            trackAddToCart,
            trackInitiateCheckout,
            trackPurchase
        }}>
            {children}
        </FacebookPixelContext.Provider>
    );
}

export function useFacebookPixel() {
    const context = useContext(FacebookPixelContext);
    if (!context) {
        throw new Error('useFacebookPixel must be used within a FacebookPixelContextProvider');
    }
    return context;
}